'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Pencil, Trash2, ShieldOff, SearchX, X } from 'lucide-react';
import Joyride from 'react-joyride';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { GLASS_CARD } from '@/components/ui/glass';
import Pagination from '@/components/pages/shared/Pagination';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';

import NewBlackListDialog from '@/components/pages/dialogs/NewBlackListDialog';
import getBlacklistTourSteps from '@/components/tour/shared/BlacklistTourSteps';
import { useListaNegra } from '@/components/hooks/useListaNegra';
import { usePageHelp, useTourVisibility } from '@/components/hooks/usePageHelp';
import {
  DLG_BTN_SECONDARY,
  DLG_BTN_DANGER,
} from '@/components/ui/dialogButtons';
import { getTourStyles } from '@/components/tour/tourStyles';

const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const rowVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, x: 20, transition: { duration: 0.2 } },
};

export default function Blacklist() {
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [runTour, setRunTour] = useState(false);
  const isMobile = window.innerWidth < 640;
  const [empresaId] = useState(() => localStorage.getItem('id_empresa') || '');
  const [userId] = useState(() => localStorage.getItem('id_usuario') || '');
  const steps = getBlacklistTourSteps(isMobile);

  const { registros, loading, eliminar, refetch, insertar, actualizar } =
    useListaNegra(empresaId);

  // El botón global de ayuda (arriba a la derecha) lanza el tour de esta
  // página. Se oculta mientras carga, que es cuando aún no hay tabla que
  // enseñar.
  usePageHelp(() => setRunTour(true), !loading);
  useTourVisibility(runTour);

  const registrosFiltrados = registros.filter((r) => {
    const clean = filter.replace(/\s/g, '').toLowerCase();
    return (
      r.telefono?.replace(/\s/g, '').includes(clean) ||
      r.email?.toLowerCase().includes(clean)
    );
  });

  const totalPages = Math.ceil(registrosFiltrados.length / pageSize) || 1;
  const currentData = registrosFiltrados.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const changePageSize = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  const openNew = () => {
    setEditing(null);
    setOpenDialog(true);
  };

  const openEdit = (registro: any) => {
    setEditing(registro);
    setOpenDialog(true);
  };

  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    // eliminar() ya muestra el toast de éxito/error y refresca la lista
    // internamente (ver useListaNegra) — no lo repetimos aquí, o el mensaje
    // sale duplicado.
    await eliminar(confirmDeleteId);
    setConfirmDeleteId(null);
  };

  return (
    <motion.div
      className="space-y-8 pb-28 lg:pb-0"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* HEADER — el botón de ayuda vive ahora arriba a la derecha, fijo
          y global (HelpButton), lanzado desde usePageHelp. */}
      <motion.div variants={itemVariants}>
        <div className="tour-title flex items-center text-sm text-gray-600 dark:text-gray-400">
          <span className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            Lista negra
          </span>
        </div>
      </motion.div>

      <Joyride
        steps={steps}
        run={runTour}
        continuous
        showSkipButton
        spotlightClicks
        locale={{
          back: 'Atrás',
          close: 'Cerrar',
          last: 'Finalizar',
          next: 'Siguiente',
          skip: 'Saltar',
        }}
        callback={({ status }) => {
          if (['finished', 'skipped'].includes(status)) setRunTour(false);
        }}
        styles={getTourStyles(isMobile)}
      />

      {/* Filter + Add */}
      <motion.div
        variants={itemVariants}
        className="flex flex-col sm:flex-row gap-3 justify-between items-center"
      >
        <Input
          type="text"
          placeholder="Filtrar por teléfono o email"
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value);
            setPage(1);
          }}
          className="tour-filter-input w-full sm:w-1/2 dark:bg-white/5 dark:border-white/10 dark:text-white"
        />

        {registros.length > 0 && (
          <Button
            onClick={openNew}
            className="rounded-lg text-white font-medium shadow-md bg-[#103254] hover:bg-[#0d2742] px-6 py-2 tour-add-btn w-full sm:w-auto transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" /> Añadir número
          </Button>
        )}
      </motion.div>

      <NewBlackListDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        initialData={editing}
        onSaved={() => {
          setOpenDialog(false);
          refetch();
        }}
        empresaId={empresaId}
        userId={userId}
        insertar={insertar}
        actualizar={actualizar}
      />

      {/* Delete confirm dialog (bottom sheet) */}
      <Dialog
        open={!!confirmDeleteId}
        onOpenChange={(o) => !o && setConfirmDeleteId(null)}
      >
        <DialogContent
          className="
            z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
            [&>button:last-child]:hidden
            flex flex-col
            !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
            w-full max-w-none rounded-t-2xl rounded-b-none
            data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
            sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
            sm:w-[95%] sm:max-w-lg sm:mx-auto sm:h-auto sm:rounded-xl sm:shadow-xl
          "
        >
          <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl font-semibold text-red-600 dark:text-red-400 pr-10">
                Eliminar número
              </DialogTitle>
              <DialogDescription className="text-xs text-gray-500 dark:text-gray-400">
                Esta acción no se puede deshacer.
              </DialogDescription>
            </DialogHeader>
            <button
              onClick={() => setConfirmDeleteId(null)}
              className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-4 sm:px-6 pb-5">
            <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                ¿Estás seguro de que quieres eliminar este número de la lista
                negra?
              </p>
            </div>
          </div>

          <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-col sm:flex-row gap-3 shrink-0 border-t border-gray-100 dark:border-white/10">
            <Button
              variant="outline"
              onClick={() => setConfirmDeleteId(null)}
              className={`w-full sm:flex-1 ${DLG_BTN_SECONDARY}`}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDelete}
              className={`w-full sm:flex-1 ${DLG_BTN_DANGER}`}
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* TABLE */}
      <AnimatePresence>
        {loading ? (
          <motion.div
            variants={itemVariants}
            className="flex justify-center items-center h-32"
          >
            <motion.div
              variants={rowVariants}
              className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#103254]"
            />
          </motion.div>
        ) : registros.length === 0 ? (
          <Card className={`rounded-2xl ${GLASS_CARD}`}>
            <CardContent className="py-16 text-center">
              <ShieldOff className="w-10 h-10 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
              <p className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                Sin números en la lista negra
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mb-6">
                Aún no se ha agregado ningún número bloqueado.
              </p>
              <Button
                onClick={openNew}
                className="rounded-lg text-white font-medium shadow-md bg-[#103254] hover:bg-[#0d2742] px-6 py-2 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" /> Añadir número
              </Button>
            </CardContent>
          </Card>
        ) : registrosFiltrados.length === 0 ? (
          <Card className={`rounded-2xl ${GLASS_CARD}`}>
            <CardContent className="py-16 text-center">
              <SearchX className="w-10 h-10 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
              <p className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                Sin coincidencias
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                No se encontraron registros con ese criterio.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Vista móvil - Tarjetas */}
            <div className="block lg:hidden space-y-4">
              {currentData.map((r) => (
                <motion.div
                  key={r.id_lista_negra}
                  variants={rowVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <Card className={`rounded-xl ${GLASS_CARD}`}>
                    <CardContent className="p-4 space-y-3">
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 dark:text-gray-400 text-xs">
                              Tel:
                            </span>
                            <h3 className="font-semibold text-gray-900 dark:text-white text-base">
                              {r.telefono || 'Sin teléfono'}
                            </h3>
                          </div>
                          {r.email && (
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-gray-500 dark:text-gray-400 text-xs">
                                Email:
                              </span>
                              <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                                {r.email}
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEdit(r)}
                            className="text-[#103254] dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 rounded-full tour-edit-btn-mobile hover:bg-gray-100 dark:hover:bg-white/10"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setConfirmDeleteId(r.id_lista_negra)}
                            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 rounded-full tour-delete-btn-mobile hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      {r.motivo && (
                        <div className="pt-2 border-t border-gray-200 dark:border-white/10">
                          <span className="text-gray-500 dark:text-gray-400 text-xs">
                            Motivo:
                          </span>
                          <p className="text-sm text-gray-800 dark:text-white mt-1">
                            {r.motivo}
                          </p>
                        </div>
                      )}

                      <div className="flex items-center justify-between text-sm pt-2">
                        <div>
                          <span className="text-gray-500 dark:text-gray-400 text-xs">
                            Fecha:
                          </span>
                          <p className="text-gray-800 dark:text-white">
                            {new Intl.DateTimeFormat('es-ES', {
                              dateStyle: 'medium',
                            }).format(new Date(r.fecha_creacion))}
                          </p>
                        </div>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            r.activo
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                          }`}
                        >
                          {r.activo ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Vista desktop - TABLE */}
            <Card className={`hidden lg:block rounded-2xl ${GLASS_CARD}`}>
              <CardContent className="overflow-x-auto p-0">
                <table className="min-w-full table-auto text-sm">
                  <thead className="tour-table-head bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300 uppercase text-xs sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left w-32">Teléfono</th>
                      <th className="px-4 py-3 text-left w-48">Email</th>
                      <th className="px-4 py-3 text-left">Motivo</th>
                      <th className="px-4 py-3 text-left w-40">Fecha</th>
                      <th className="px-4 py-3 text-left w-20 tour-activo-badge">
                        Activo
                      </th>
                      <th className="px-4 py-3 text-right w-28">Acciones</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100 dark:divide-white/10">
                    {currentData.map((r, idx) => (
                      <motion.tr
                        key={r.id_lista_negra}
                        variants={rowVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        className={
                          idx % 2 === 0
                            ? 'bg-white dark:bg-transparent'
                            : 'bg-gray-50 dark:bg-white/[0.03]'
                        }
                      >
                        <td className="px-4 py-3 text-gray-800 dark:text-white">
                          {r.telefono || '-'}
                        </td>

                        <td
                          className="px-4 py-3 truncate max-w-[180px] text-gray-800 dark:text-white"
                          title={r.email || '-'}
                        >
                          {r.email || '-'}
                        </td>

                        <td
                          className="px-4 py-3 truncate max-w-[200px] text-gray-800 dark:text-white"
                          title={r.motivo || '-'}
                        >
                          {r.motivo || '-'}
                        </td>

                        <td className="px-4 py-3 text-gray-800 dark:text-white">
                          {new Intl.DateTimeFormat('es-ES', {
                            dateStyle: 'medium',
                          }).format(new Date(r.fecha_creacion))}
                        </td>

                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              r.activo
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                            }`}
                          >
                            {r.activo ? 'Sí' : 'No'}
                          </span>
                        </td>

                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEdit(r)}
                              className="text-[#103254] dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 rounded-full tour-edit-btn-desktop hover:bg-gray-100 dark:hover:bg-white/10"
                              aria-label="Editar"
                            >
                              <Pencil className="w-5 h-5" />
                            </Button>

                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() =>
                                setConfirmDeleteId(r.id_lista_negra)
                              }
                              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 rounded-full tour-delete-btn-desktop hover:bg-red-50 dark:hover:bg-red-900/20"
                              aria-label="Eliminar"
                            >
                              <Trash2 className="w-5 h-5" />
                            </Button>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* Paginación */}
            <Pagination
              page={page}
              totalPages={totalPages}
              totalItems={registrosFiltrados.length}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={changePageSize}
              itemLabel="números"
              className="tour-pagination"
            />
          </>
        )}
      </AnimatePresence>
    </motion.div>
  );
}